import { createContext, use, useEffect, useState } from "react";

import type { CoreTheme, ResolvedTheme, Theme } from "../theme/themeRegistry";
import {
  getThemeClasses,
  isTheme,
  resolveTheme,
  toCoreTheme,
} from "../theme/themeRegistry";

const DEFAULT_THEME: Theme = "system";
type ThemeProviderProps = {
  children: React.ReactNode;
  storageKey: string;
  enableMarketingBehavior?: boolean;
  storage: Storage;
};
type ThemeProviderState = {
  theme: Theme;
  isDarkTheme: boolean;
  isLightTheme: boolean;
  appliedTheme?: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: ResolvedTheme;
  darkOrLightTheme: CoreTheme;
};
const ThemeProviderContext = createContext<ThemeProviderState | null>(null);
const getSystemTheme = (): CoreTheme => {
  if (typeof window === "undefined") {
    return "dark";
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};
export function ThemeProvider({
  children,
  enableMarketingBehavior = false,
  storage,
  storageKey,
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (storage.getItem(storageKey) ?? DEFAULT_THEME) as Theme,
  );
  const [systemTheme, setSystemTheme] = useState<CoreTheme>(getSystemTheme);
  const baseResolvedTheme = resolveTheme(theme, systemTheme);
  const appliedTheme: Theme | undefined =
    enableMarketingBehavior && baseResolvedTheme === "light"
      ? "dark"
      : undefined;
  const resolvedTheme = resolveTheme(appliedTheme ?? theme, systemTheme);
  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? "dark" : "light");
    };
    mediaQuery.addEventListener("change", handleSystemThemeChange);
    return () => {
      mediaQuery.removeEventListener("change", handleSystemThemeChange);
    };
  }, []);
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");
    const resolvedTheme = resolveTheme(appliedTheme ?? theme, systemTheme);
    root.classList.add(...getThemeClasses(resolvedTheme));
  }, [appliedTheme, theme, systemTheme]);
  const darkOrLightTheme: CoreTheme = toCoreTheme(resolvedTheme);
  const state: ThemeProviderState = {
    appliedTheme,
    resolvedTheme,
    setTheme: (theme: Theme) => {
      storage.setItem(storageKey, theme);
      setTheme(theme);
    },
    theme,
    darkOrLightTheme,
    isDarkTheme: darkOrLightTheme === "dark",
    isLightTheme: darkOrLightTheme === "light",
  };
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === storageKey && event.newValue) {
        if (!isTheme(event.newValue)) {
          return;
        }
        setTheme(event.newValue);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [storageKey]);
  return (
    <ThemeProviderContext.Provider {...props} value={state}>
      {children}
    </ThemeProviderContext.Provider>
  );
}
export function useTheme(): ThemeProviderState {
  const context = use(ThemeProviderContext);
  if (context == null) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  const resolvedTheme = context.appliedTheme
    ? resolveTheme(context.appliedTheme, getSystemTheme())
    : context.resolvedTheme;
  const darkOrLightTheme: CoreTheme = toCoreTheme(resolvedTheme);
  return {
    ...context,
    isDarkTheme: darkOrLightTheme === "dark",
    isLightTheme: darkOrLightTheme === "light",
    resolvedTheme,
    theme: context.theme,
    darkOrLightTheme,
  };
}
